import { BrowserModule } from '@angular/platform-browser';
import {
    NgModule,
    APP_INITIALIZER,
    CUSTOM_ELEMENTS_SCHEMA,
    NO_ERRORS_SCHEMA,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { WellTrackBoostApp } from './app.component';
import { ApiService } from './lib/api.service';
import { ApiRestService } from './lib/api-rest.service';
import { AuthService } from './lib/auth.service';
import { LocationService } from './lib/location.service';
import { LogService } from './lib/log.service';
import { MoodcheckService } from './lib/moodcheck.service';
import { StorageService } from './lib/storage.service';
import { UserService } from './lib/user.service';
import { UtilityService } from './lib/utility.service';
import { ModalService } from './lib/modal.service';
import { GraphService } from './lib/graph.service';
import { FileService } from './lib/file.service';
import { OrganizationService } from './lib/organization.service';
import { VideoService } from './lib/video.service';
import { SsoService } from './lib/sso.service';
import { ConfigService } from './lib/config.service';

import { PublicPortalTemplate } from './pages/public-portal/public-portal.component';
import { PublicPortalFullTemplate } from './pages/public-portal-full/public-portal-full';
import { RegisterPaymentPage } from './pages/register-payment/register-payment';
import { AuthPortalTemplate } from './pages/auth-portal/auth-portal.component';
import { DashboardPage } from './pages/dashboard/dashboard.component';

import { AuthGuard } from './guards/auth.guard';
import { SeriesAccessGuard } from './guards/series-access.guard';
import { AssociatedGuard } from './guards/associated.guard';
import { OrgConfigGuard } from './guards/orgConfig.guard';
import { AclGuard } from './guards/acl.guard';
import { OrganizationEditPage } from './pages/organization-edit/organization-edit.component';
import { OrganizationViewPage } from './pages/organization-view/organization-view.component';
import { UserListingAdminPage } from './pages/user-listing-admin/user-listing-admin.component';
import { WtComponentModule } from './components/welltrack-components.module';
import { CookieService } from 'ngx-cookie-service';
import { Angulartics2Module } from 'angulartics2';
import { AppRoutingModule } from './app-routing.module';
import { SettingsPage } from './pages/settings/settings';
import { OrgadminSettingComponent } from './pages/orgadmin-setting/orgadmin-setting.component';
import { MysettingComponent } from './pages/mysetting/mysetting.component';
import { MyDataComponent } from './pages/my-data/my-data.component';
import { EmergencyContactPage } from './pages/emergency-contact/emergency-contact';
import { ResourcesPage } from './pages/resources/resources';
import { ResourcesDetailComponent } from './pages/resources-detail/resources-detail.component';
import { CommunityComponent } from './pages/community/community.component';
import { TheoryComponent } from './pages/theory/theory.component';
import { CoursesComponent } from './pages/courses/courses.component';
import { CoursesListingComponent } from './pages/courses-listing/courses-listing.component';
import { AssessmentsPage } from './pages/assessments/assessments';
import { DasAssessmentPage } from './pages/das-assessment/das-assessment';
import { MentalWellbeingAssessmentComponent } from './pages/mental-wellbeing-assessment/mental-wellbeing-assessment.component';
import { CrafftAssessmentComponent } from './pages/crafft-assessment/crafft-assessment.component';
import { ResilienceListingPage } from './pages/resilience-listing/resilience-listing';
import { AddictionAssessmentPage } from './pages/addiction-assessment/addiction-assessment';
import {
    HttpClient,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import {
    TranslateModule,
    TranslateLoader,
    TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
// import { DndModule } from "ng2-dnd";
import { MoodcheckModule } from './components/moodcheck-modal/moodcheck.module';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { MenuService } from './lib/menu.service';
import { DateService, UrlService, FooterService } from './lib/url.service';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';

import { EventManagerService } from './lib/event-manager.service';
import { MiniCalendarComponent } from './components/calendar/mini.calendar.component';
import { StaticPagesModule } from './pages/_static/static-pages.module';
import { DashboardAdminComponent } from './pages/dashboard-admin/dashboard-admin.component';
import { OrganizationListingPage } from './pages/organization-listing/organization-listing.component';
import { UserProfileDetailsComponent } from './pages/user-profile-details/user-profile-details.component';
import { UserEditComponent } from './pages/user-edit/user-edit.component';
import { UserCreateComponent } from './pages/user-create/user-create.component';
import { UserInviteComponent } from './pages/user-invite/user-invite.component';
import { ActivityInfoPage } from './pages/activity-info/activity-info.component';

import { AutoLogoutService } from './lib/auto-logout';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { CalendarPage } from 'app/pages/calendar/calendar.component';
// import { TooltipModule } from "ng2-tooltip";
// import { NgxMatDatetimePickerModule, NgxMatTimepickerModule, NgxMatNativeDateModule } from '@angular-material-components/datetime-picker';
import { NgxStripeModule } from 'ngx-stripe';
import { ReportsPage } from 'app/pages/reports/reports';
import { OnboardingPage } from 'app/pages/onboarding/onboarding';
import { DemographicPage } from 'app/pages/demographic/demographic';
import { ManageResourcesPage } from 'app/pages/manage-resources/manage-resources';
import { environment } from '../environments/environment';
// import { Angular2FontawesomeModule } from "angular2-fontawesome/angular2-fontawesome";
import { ErrorPopup } from './components/alerts/error-popup/error-popup';
import { ConfirmPopup } from './components/alerts/confirm-popup/confirm-popup';
import { ModalPopup } from './components/alerts/modal-popup/modal-popup';
import { CalendarService } from './lib/calendar.service';
import { PermissionsService } from './lib/permissions.service';
import { ResiliencePage } from './pages/resilience/resilience';
import { AssessmentsListingPage } from './pages/assessments-listing/assessments-listing.component';
import { ResourceImportPage } from './pages/resources-import/resources-import.component';
import { AdminCourseListingPage } from './pages/admin-course-listing/admin-course-listing.component';
import { AuthSideMenuTemplate } from './pages/auth-side-menu/auth-side-menu.component';
import { UserDatedEventsListingPage } from './pages/user-dated-events-listing/user-dated-events-listing.component';
import { NotFoundPage } from './pages/not-found/not-found';
import { AuthModule } from './modules/auth/auth.module';
import { MenuModule } from './modules/menu/menu.module';
import { PublicModule } from './modules/public/public.module';
import { SharedModule } from './shared/shared.module';
import { PracticeModule } from './modules/practice/practice.module';
import { ProfessionalModule } from './modules/professional/professional.module';
import { ResourceView } from './pages/resource-view/resource-view.component';
import { DynamicformPageComponent } from './components/dynamicform-page/dynamicform-page.component';
import { UserCalendarComponent } from 'app/pages/user-calendar/user-calendar.component';
import { CustomAnnouncementsComponent } from './components/admin/custom-announcements/custom-announcements.component';
import { TemplatesComponent } from './components/admin/templates/templates.component';
import { AnnouncementsComponent } from './components/admin/announcements/announcements.component';
import { TemplateEditComponent } from './components/admin/templates/template-edit/template-edit.component';
import { AnnouncementEditComponent } from './components/admin/announcements/announcement-edit/announcement-edit.component';
import { BadgesPage } from './pages/badges/badges';
import { AnnouncementsOrgadminComponent } from './components/admin/announcements-orgadmin/announcements-orgadmin.component';
import { AnnouncementEditOrgadminComponent } from './components/admin/announcements-orgadmin/announcement-edit/announcement-edit-orgadmin.component';
// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(
        http,
        '/assets/i18n/',
        '.json?cb=' + new Date().getTime()
    );
}
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { DragDropModule } from '@angular/cdk/drag-drop';
// import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';

import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { CommonModule } from '@angular/common';

import { AutofocusDirective } from './directives/auto-focus.directive';
import { PhoneNumberValidatorDirective } from './directives/phone-number.directive';
import { AdditionalResourcesPage } from './pages/additional-resources/additional-resources';
import { AdditionalResourceListingPage } from './pages/additional-resource-listing/additional-resource-listing.component';
import { AutoLogoutIdle } from './lib/auto-logout-idle';

export const configFactory = (configService: ConfigService) => {
    return () => configService.load();
};
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { MessagingService } from './lib/message-service';
import { InternalUserListingComponent } from './pages/internal-user-listing/internal-user-listing.component';
import { DashboardSuperAdminComponent } from './components/dashboards/dashboard-super-admin/dashboard-super-admin.component';
import { DynamicFormDataService } from './components/dynamic-form/dynamic-form.service';
import { AssessmentViewComponent } from './pages/assessment-view/assessment-view.component';
import { TranslationServiceHelper } from './lib/translation.service';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { CourseVideoComponent } from './pages/course-video/course-video.component';
import { SeriesDashboardComponent } from './pages/series-dashboard/series-dashboard.component';
import { SeriesCategoryDetailsComponent } from './pages/series-category-details/series-category-details.component';
import { SeriesVoicesComponent } from './pages/series-voices/series-voices.component';
import { SeriesVoicesStoriesComponent } from './pages/series-voices-stories/series-voices-stories.component';
import { SeriesVoicesBurningquestionsComponent } from './pages/series-voices-burningquestions/series-voices-burningquestions.component';
import { SeriesDailyContentComponent } from './pages/series-daily-content/series-daily-content.component';
import { SeriesVoicesPlayerComponent } from './pages/series-voices-player/series-voices-player.component';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { NgxPaginationModule } from 'ngx-pagination';
import { VgCoreModule } from '@videogular/ngx-videogular/core';
import { VgControlsModule } from '@videogular/ngx-videogular/controls';
import { VgOverlayPlayModule } from '@videogular/ngx-videogular/overlay-play';
import { VgBufferingModule } from '@videogular/ngx-videogular/buffering';
import {
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
} from '@danielmoncada/angular-datetime-picker';
import { OrgSeriesContentsComponent } from './pages/org-series-contents/org-series-contents.component';
import { OrgSeriesContentViewAllComponent } from './pages/org-series-content-view-all/org-series-content-view-all.component';
import { SeriesReadsContentComponent } from "./pages/series-reads-content/series-reads-content.component";
// const routes: Routes = [];
@NgModule({
    declarations: [
        WellTrackBoostApp,
        RegisterPaymentPage,
        PublicPortalTemplate,
        PublicPortalFullTemplate,
        AuthPortalTemplate,
        DashboardPage,
        OrganizationEditPage,
        OrganizationViewPage,
        UserListingAdminPage,
        SettingsPage,
        OrgadminSettingComponent,
        MysettingComponent,
        MyDataComponent,
        EmergencyContactPage,
        ResourcesPage,
        ResourcesDetailComponent,
        AssessmentsPage,
        DasAssessmentPage,
        MentalWellbeingAssessmentComponent,
        CrafftAssessmentComponent,
        AddictionAssessmentPage,
        CommunityComponent,
        TheoryComponent,
        CoursesComponent,
        CoursesListingComponent,
        ResilienceListingPage,
        DashboardAdminComponent,
        OrganizationListingPage,
        UserProfileDetailsComponent,
        UserEditComponent,
        UserCreateComponent,
        UserInviteComponent,
        ActivityInfoPage,
        CalendarPage,
        ReportsPage,
        OnboardingPage,
        DemographicPage,
        ManageResourcesPage,
        ErrorPopup,
        ConfirmPopup,
        ModalPopup,
        MiniCalendarComponent,
        ResiliencePage,
        PhoneNumberValidatorDirective,
        AssessmentsListingPage,
        ResourceImportPage,
        AdminCourseListingPage,
        AuthSideMenuTemplate,
        UserDatedEventsListingPage,
        NotFoundPage,
        AdditionalResourcesPage,
        AdditionalResourceListingPage,
        ResourceView,
        InternalUserListingComponent,
        DashboardSuperAdminComponent,
        AssessmentViewComponent,
        CourseVideoComponent,
        DynamicformPageComponent,
        UserCalendarComponent,
        SeriesDashboardComponent,
        SeriesCategoryDetailsComponent,
        SeriesVoicesComponent,
        SeriesVoicesStoriesComponent,
        SeriesVoicesBurningquestionsComponent,
        SeriesDailyContentComponent,
        SeriesVoicesPlayerComponent,
        SeriesReadsContentComponent,
        BadgesPage,
        CustomAnnouncementsComponent,
        TemplatesComponent,
        AnnouncementsComponent,
        AnnouncementEditComponent,
        TemplateEditComponent,
        AnnouncementsOrgadminComponent,
        AnnouncementEditOrgadminComponent,
        OrgSeriesContentsComponent,
        OrgSeriesContentViewAllComponent,
    ],
    schemas: [NO_ERRORS_SCHEMA, CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [WellTrackBoostApp],
    imports: [
        BrowserModule,
        FormsModule,
        AppRoutingModule,
        CarouselModule,
        WtComponentModule,
        NgxPaginationModule,
        StaticPagesModule,
        Angulartics2Module.forRoot(),
        ReactiveFormsModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        // DndModule.forRoot(),
        MoodcheckModule,
        CKEditorModule,
        CalendarModule.forRoot({
            provide: DateAdapter,
            useFactory: adapterFactory,
        }),
        // TooltipModule,
        OwlDateTimeModule,
        OwlNativeDateTimeModule,
        // NgxMatDatetimePickerModule,
        // NgxMatTimepickerModule,
        // NgxMatNativeDateModule,
        NgxStripeModule.forRoot(environment.stripe.api_key),
        // Angular2FontawesomeModule,
        MatAutocompleteModule,
        MatNativeDateModule,
        MatFormFieldModule,
        FormsModule,
        MatDatepickerModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatButtonModule,
        MatSliderModule,
        MatDialogModule,
        MatIconModule,
        MatChipsModule,
        DragDropModule,
        // NgxMaterialTimepickerModule,
        AuthModule,
        MenuModule,
        PublicModule,
        SharedModule,
        PracticeModule,
        ProfessionalModule,
        NgIdleKeepaliveModule.forRoot(),
        // Ng2SearchPipeModule,
        LazyLoadImageModule,
        MatFormFieldModule,
        VgCoreModule,
        VgControlsModule,
        VgOverlayPlayModule,
        VgBufferingModule,
        CommonModule,
        RouterModule,
        NgxIntlTelInputModule,
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: configFactory,
            deps: [ConfigService],
            multi: true,
        },
        provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
        provideMessaging(() => getMessaging()),
        ApiService,
        ApiRestService,
        AuthService,
        LocationService,
        LogService,
        CookieService,
        MoodcheckService,
        StorageService,
        UserService,
        UtilityService,
        AuthGuard,
        SeriesAccessGuard,
        AssociatedGuard,
        OrgConfigGuard,
        AclGuard,
        ModalService,
        CKEditorModule,
        GraphService,
        AutoLogoutService,
        AutoLogoutIdle,
        MatDatepickerModule,
        EventManagerService,
        CalendarService,
        PermissionsService,
        FileService,
        OrganizationService,
        VideoService,
        SsoService,
        MenuService,
        MessagingService,
        DynamicFormDataService,
        UrlService,
        DateService,
        FooterService,
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppModule {
    constructor(private translateService: TranslateService) {
        TranslationServiceHelper.setTranslateService(translateService);
    }
}
